import { useMemo } from "react"
import {
  MAP_MARKER_GRAY_URL,
  MAP_MARKER_GREEN_URL,
  MAP_SUBJECT_ICON_URL,
} from "@/constants/MapDataUrlImages"
import { type AnalysisCompType, type PropertyType } from "@/generated/graphql"
import config from "@/config"
import { calculatedDistance, calculateZoomLevel } from "@/utils/CompUtils"
import { parseCoordinate } from "@/utils/NumberUtils"
import { calculateVisibleComps } from "@/utils/MapUtils"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"

interface UseGoogleStaticMapParams {
  property: PropertyType
  comps: AnalysisCompType[]
  maxDistance?: number // Optional parameter to set the maximum distance for comps to use
}

const useGoogleStaticMap = ({
  property,
  comps,
  maxDistance = 1.0,
}: UseGoogleStaticMapParams): {
  mapUrl: string
  aspectRatio: number
} => {
  const { isWeb, screenWidth } = useDeviceAttributes()

  return useMemo(() => {
    const latitude = parseCoordinate(property.latitude)
    const longitude = parseCoordinate(property.longitude)
    const mapWidth: number = isWeb ? 640 : screenWidth
    const mapHeight: number = 95 // Define map height for zoom calculations & aspectRatio
    const aspectRatio = mapWidth / mapHeight

    if (comps.length === 0) {
      return { mapUrl: "", aspectRatio }
    }

    // Filter comps based on distance
    const filteredComps = comps.filter((comp) => {
      const compLat = parseCoordinate(comp.arvcomp?.latitude)
      const compLon = parseCoordinate(comp.arvcomp?.longitude)
      const distance = calculatedDistance(latitude, longitude, compLat, compLon)
      return distance !== null && distance <= maxDistance
    })

    let markersString = filteredComps
      .map((comp) => {
        const iconUrl = comp.isSelected
          ? MAP_MARKER_GREEN_URL
          : MAP_MARKER_GRAY_URL
        const compLat = parseCoordinate(comp.arvcomp?.latitude)
        const compLon = parseCoordinate(comp.arvcomp?.longitude)
        return `&markers=anchor:11,11|icon:${iconUrl}|${compLat},${compLon}&scale=2`
      })
      .join("")

    // Add the subject property marker at the center
    markersString += `&markers=anchor:11,11|icon:${MAP_SUBJECT_ICON_URL}|${latitude},${longitude}&scale=2`

    const zoomLevel = calculateZoomLevel(
      Math.min(
        ...filteredComps.map(
          (comp) => parseCoordinate(comp.arvcomp?.latitude) || Infinity
        )
      ),
      Math.max(
        ...filteredComps.map(
          (comp) => parseCoordinate(comp.arvcomp?.latitude) || -Infinity
        )
      ),
      Math.min(
        ...filteredComps.map(
          (comp) => parseCoordinate(comp.arvcomp?.longitude) || Infinity
        )
      ),
      Math.max(
        ...filteredComps.map(
          (comp) => parseCoordinate(comp.arvcomp?.longitude) || -Infinity
        )
      ),
      mapWidth,
      mapHeight
    )

    const visibleCompsCount = calculateVisibleComps(
      latitude || 0,
      longitude || 0,
      filteredComps,
      zoomLevel,
      mapWidth,
      mapHeight
    )

    const fixedZoomLevel = 13
    const apiKey = config.STATIC_GOOGLE_MAPS_API_KEY
    const MAP_ID: string = "f33ea908b32cdf37"
    const mapUrl = `https://maps.googleapis.com/maps/api/staticmap?&map_id=${MAP_ID}&center=${latitude},${longitude}&zoom=${visibleCompsCount <= 1 ? fixedZoomLevel : zoomLevel}&size=${Math.ceil(mapWidth)}x100&maptype=roadmap${markersString}&scale=4&format=png32&key=${apiKey}`

    return { mapUrl, aspectRatio }
  }, [property, comps, maxDistance])
}

export default useGoogleStaticMap
